import { useState, useEffect, ChangeEventHandler } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';
import { passwordCheck } from 'utils/signRegex';

type PasswordDoubleCheckType = 'pass' | 'wrong' | 'second_none' | 'none';

/** 회원가입 - 계정설정에서 패스워드 확인 input의 상태를 체크하는 커스텀 훅
 *
 * @param input_ref input창의 ref / 패스워드
 * @returns helperText의 상태 / set helperText의 상태 / helperText
 */
const usePasswordDoubleCheck = <T extends HTMLElement>(
	input_ref: RefObject<T>,
	password: string,
): [
	string,
	PasswordDoubleCheckType,
	Record<PasswordDoubleCheckType, string>,
	(password_double?: string) => void,
	ChangeEventHandler<HTMLInputElement>,
] => {
	// 패스워드의 value
	const [password_double, setPasswordDouble] = useState('');

	// 패스워드 확인 input의 상태 (helper text의 상태)
	const [password_double_check, setPasswordDoubleCheck] =
		useState<PasswordDoubleCheckType>('none');

	const passwordDoubleStateCheck = (password_double?: string) => {
		if (password_double === undefined) return;

		if (!password) setPasswordDoubleCheck('none');
		else if (password_double === '') setPasswordDoubleCheck('second_none');
		else if (password_double !== password) setPasswordDoubleCheck('wrong');
		else setPasswordDoubleCheck('pass');
	};

	useEffect(() => {
		if (!input_ref.current || !password) return;

		const focustOutEvent = (e: FocusEvent) => {
			const target = e.currentTarget as HTMLInputElement;

			if (!target) return;
			passwordDoubleStateCheck(target.value);
		};

		input_ref.current.addEventListener('focusout', focustOutEvent);

		return () => input_ref.current?.removeEventListener('focusout', focustOutEvent);
	}, [password_double_check, input_ref, password]);

	const helper_text: Record<PasswordDoubleCheckType, string> = {
		none: '',
		wrong: '비밀번호가 일치하지 않습니다.',
		second_none: '비밀번호를 입력해주세요.',
		pass: '',
	};

	const passwordDoubleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const value = e.currentTarget.value;

		setPasswordDouble(value);
	};

	return [
		password_double,
		password_double_check,
		helper_text,
		passwordDoubleStateCheck,
		passwordDoubleChange,
	];
};

export default usePasswordDoubleCheck;
